import {
  keepPreviousData,
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  approveCreateProductRequest,
  approveMapProductRequest,
  createSupplierProductRequest,
  listPendingProductRequests,
  listPendingProductRequestsPage,
  listSupplierProductRequests,
  listSupplierProductRequestsPage,
  rejectProductRequest,
} from "./api";
import type {
  ApproveCreateProductRequestPayload,
  ApproveMapProductRequestPayload,
  CreateProductRequestPayload,
  ProductRequestPageQuery,
  RejectProductRequestPayload,
} from "./types";

export const SUPPLIER_PRODUCT_REQUESTS_QUERY_KEY = [
  "supplier-product-requests",
];
export const PENDING_PRODUCT_REQUESTS_QUERY_KEY = ["pending-product-requests"];
export const PRODUCT_REQUEST_PAGE_SIZE = 10;
export const PRODUCT_REQUEST_COUNT_LIMIT = 1;
const ALERTS_QUERY_KEY = ["alerts"];
const ALERTS_COUNT_QUERY_KEY = ["alerts", "count"];

function invalidateQueryKeys(qc: QueryClient, queryKeys: QueryKey[]) {
  return Promise.all(
    queryKeys.map((queryKey) => qc.invalidateQueries({ queryKey })),
  );
}

export function useSupplierProductRequests(enabled = true) {
  return useQuery({
    queryKey: SUPPLIER_PRODUCT_REQUESTS_QUERY_KEY,
    queryFn: () => listSupplierProductRequests(2000, 0),
    enabled,
  });
}

function normalizeProductRequestPageQuery(
  query: ProductRequestPageQuery,
  includeStatus: boolean,
  includeRequestId = false,
): ProductRequestPageQuery {
  const normalized: ProductRequestPageQuery = {
    limit: query.limit,
    offset: query.offset,
    search: query.search.trim(),
  };
  if (includeStatus && query.status) normalized.status = query.status;
  const date = query.date?.trim();
  const requestId = query.requestId?.trim();
  if (date) normalized.date = date;
  if (includeRequestId && requestId) normalized.requestId = requestId;
  return normalized;
}

export function useSupplierProductRequestsPage(
  query: ProductRequestPageQuery,
  enabled = true,
) {
  const normalizedQuery = normalizeProductRequestPageQuery(query, true);

  return useQuery({
    queryKey: [...SUPPLIER_PRODUCT_REQUESTS_QUERY_KEY, "page", normalizedQuery],
    queryFn: ({ signal }) =>
      listSupplierProductRequestsPage(normalizedQuery, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useSupplierProductRequestCount(enabled = true) {
  return useQuery({
    queryKey: [...SUPPLIER_PRODUCT_REQUESTS_QUERY_KEY, "count"],
    queryFn: ({ signal }) =>
      listSupplierProductRequestsPage(
        { limit: PRODUCT_REQUEST_COUNT_LIMIT, offset: 0, search: "" },
        signal,
      ),
    enabled,
    select: (page) => page.total,
  });
}

export function useCreateSupplierProductRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProductRequestPayload) =>
      createSupplierProductRequest(payload),
    onSuccess: () =>
      invalidateQueryKeys(qc, [
        SUPPLIER_PRODUCT_REQUESTS_QUERY_KEY,
        PENDING_PRODUCT_REQUESTS_QUERY_KEY,
        ALERTS_QUERY_KEY,
        ALERTS_COUNT_QUERY_KEY,
      ]),
  });
}

export function usePendingProductRequests(enabled = true) {
  return useQuery({
    queryKey: PENDING_PRODUCT_REQUESTS_QUERY_KEY,
    queryFn: () => listPendingProductRequests(2000, 0),
    enabled,
  });
}

export function usePendingProductRequestsPage(
  query: ProductRequestPageQuery,
  enabled = true,
) {
  const normalizedQuery = normalizeProductRequestPageQuery(query, false, true);

  return useQuery({
    queryKey: [...PENDING_PRODUCT_REQUESTS_QUERY_KEY, "page", normalizedQuery],
    queryFn: ({ signal }) =>
      listPendingProductRequestsPage(normalizedQuery, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function usePendingProductRequestById(
  requestId: string | undefined,
  enabled = true,
) {
  const normalizedRequestId = requestId?.trim() ?? "";

  return useQuery({
    queryKey: [
      ...PENDING_PRODUCT_REQUESTS_QUERY_KEY,
      "by-id",
      normalizedRequestId,
    ],
    queryFn: ({ signal }) =>
      listPendingProductRequestsPage(
        {
          limit: PRODUCT_REQUEST_COUNT_LIMIT,
          offset: 0,
          search: "",
          requestId: normalizedRequestId,
        },
        signal,
      ),
    enabled: enabled && Boolean(normalizedRequestId),
    select: (page) => page.items[0],
  });
}

export function usePendingProductRequestCount(enabled = true) {
  return useQuery({
    queryKey: [...PENDING_PRODUCT_REQUESTS_QUERY_KEY, "count"],
    queryFn: ({ signal }) =>
      listPendingProductRequestsPage(
        { limit: PRODUCT_REQUEST_COUNT_LIMIT, offset: 0, search: "" },
        signal,
      ),
    enabled,
    select: (page) => page.total,
  });
}

export function useApproveCreateProductRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ApproveCreateProductRequestPayload;
    }) => approveCreateProductRequest(id, payload),
    onSuccess: () =>
      invalidateQueryKeys(qc, [
        PENDING_PRODUCT_REQUESTS_QUERY_KEY,
        SUPPLIER_PRODUCT_REQUESTS_QUERY_KEY,
        ["supplier-catalog-products"],
        ["products"],
        ALERTS_QUERY_KEY,
        ALERTS_COUNT_QUERY_KEY,
      ]),
  });
}

export function useApproveMapProductRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ApproveMapProductRequestPayload;
    }) => approveMapProductRequest(id, payload),
    onSuccess: () =>
      invalidateQueryKeys(qc, [
        PENDING_PRODUCT_REQUESTS_QUERY_KEY,
        SUPPLIER_PRODUCT_REQUESTS_QUERY_KEY,
        ["supplier-catalog-products"],
        ALERTS_QUERY_KEY,
        ALERTS_COUNT_QUERY_KEY,
      ]),
  });
}

export function useRejectProductRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: RejectProductRequestPayload;
    }) => rejectProductRequest(id, payload),
    onSuccess: () =>
      invalidateQueryKeys(qc, [
        PENDING_PRODUCT_REQUESTS_QUERY_KEY,
        SUPPLIER_PRODUCT_REQUESTS_QUERY_KEY,
        ALERTS_QUERY_KEY,
        ALERTS_COUNT_QUERY_KEY,
      ]),
  });
}
